import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import LeLiveHeading from './LeLiveHeading'
import LineCalendarAltIcon from 'LuxKit/Icons/line/LineCalendarAltIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import Image from 'components/Common/Image'
import LeLiveGetTicketsButton from './LeLiveGetTicketsButton'
import LeLiveAddToCalendar from './LeLiveAddToCalendar'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const SectionWrapper = styled.section`
  background-color: #f3f1ea;
`

const ImageWrapper = styled.div`
  border-radius: ${rem(8)};
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    object-fit: cover;
  }

  ${mediaQueryUp.desktop} {
    flex: 1 0 0;
    min-width: 0;
    align-self: stretch;

    img {
      height: 100%;
    }
  }
`

const TextContainer = styled(Group)`
  ${mediaQueryUp.desktop} {
    flex: 1 0 0;
    min-width: 0;
    padding-left: ${rem(59)};
    padding-right: ${rem(96)};
  }
`

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(48)};
  height: ${rem(48)};
  background-color: #ffffff;
  border-radius: 50%;
  color: #363a45;
  flex-shrink: 0;
  box-shadow:
    0 1px 1.5px rgba(0, 0, 0, 0.07),
    0 1.5px 5px rgba(0, 0, 0, 0.05),
    0 5px 10px rgba(0, 0, 0, 0.05),
    0 15px 32px rgba(0, 0, 0, 0.04);
`

interface Props {
  hasRegistration: boolean
}

function LeLiveEventDetails({ hasRegistration }: Props) {
  const intl = useIntl()

  return (
    <SectionWrapper id="event-details">
      <Sheet padding="56 20" desktopPadding="64 0">
        <LayoutContainer>
          <Group
            direction="vertical"
            gap={32}
            desktopDirection="horizontal"
            desktopGap={0}
            desktopVerticalAlign="stretch"
          >
            <ImageWrapper>
              <Image
                id="gma2bf9harb2yvqf1fva"
                alt={intl.formatMessage({
                  id: 'eEBFN7MyX3BlEl2ipkDp',
                  defaultMessage: 'Royal Exhibition Building, Melbourne',
                  description: 'Accessible alt text for the LE Live event venue hero image',
                })}
                width={640}
                height={430}
                dpr={2}
                enhancedFormats
                fit="center"
                objectFit="cover"
              />
            </ImageWrapper>

            <TextContainer direction="vertical" gap={32} desktopGap={40} desktopVerticalAlign="center">
              <LeLiveHeading variant="heading1alt" as="h2">
                {intl.formatMessage({
                  id: 'KqtR2rvN4L84t4tG52v3',
                  defaultMessage: 'Event details',
                  description: 'Section heading for the event details area on the LE Live event landing page',
                })}
              </LeLiveHeading>

              <Group direction="vertical" gap={24}>
                <Group direction="horizontal" gap={16}>
                  <IconCircle>
                    <LineCalendarAltIcon />
                  </IconCircle>
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="large" weight="bold">
                      {intl.formatMessage({
                        id: 'OQDMgNEm58a28mpFcvIX',
                        defaultMessage: 'When',
                        description: 'Bold label for the date and time information row in the LE Live event details section',
                      })}
                    </BodyText>
                    <BodyText variant="large">
                      {intl.formatMessage({
                        id: 'efHwabwsZDUB48zEHcSr',
                        defaultMessage: 'Saturday 16 May & Sunday 17 May 2026',
                        description: 'Specific calendar dates displayed in the event details section of the LE Live event page',
                      })}
                    </BodyText>
                    <BodyText variant="medium" colour="neutral-two">
                      {intl.formatMessage({
                        id: 'I4ArKNl7mRlm62-XNi_C',
                        defaultMessage: '10:00am – 6:00pm',
                        description: 'Opening and closing times displayed in the event details section of the LE Live event page',
                      })}
                    </BodyText>
                  </Group>
                </Group>

                <Group direction="horizontal" gap={16}>
                  <IconCircle>
                    <LineMapMarkerIcon />
                  </IconCircle>
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="large" weight="bold">
                      {intl.formatMessage({
                        id: 'KBcAFSh7h7995VqZm2kq',
                        defaultMessage: 'Where',
                        description: 'Bold label for the location information row in the LE Live event details section',
                      })}
                    </BodyText>
                    <BodyText variant="large">
                      {intl.formatMessage({
                        id: '86Usnz2CXGqvu-brgn3B',
                        defaultMessage: 'Royal Exhibition Building',
                        description: 'Name of the venue displayed in the location row of the LE Live event details section',
                      })}
                    </BodyText>
                    <BodyText variant="medium" colour="neutral-two">
                      {intl.formatMessage({
                        id: 'NRelv2BsbqzSKZdJ7MnK',
                        defaultMessage: '9 Nicholson St, Carlton VIC 3053',
                        description: 'Street address of the venue displayed in the location row of the LE Live event details section',
                      })}
                    </BodyText>
                  </Group>
                </Group>

                <Group direction="horizontal" gap={16}>
                  <IconCircle>
                    <LineTicketIcon />
                  </IconCircle>
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="large" weight="bold">
                      {intl.formatMessage({
                        id: '_JZhTGmZ5AoXLtCjoyjs',
                        defaultMessage: 'Tickets',
                        description: 'Bold label for the tickets and pricing row in the LE Live event details section',
                      })}
                    </BodyText>
                    <BodyText variant="large">
                      {intl.formatMessage({
                        id: 'ubVA0q28a88VV3j5dALv',
                        defaultMessage: 'FREE entry for members',
                        description: 'Summary of ticket pricing for members and walk-ups in the LE Live event details section',
                      })}
                    </BodyText>
                  </Group>
                </Group>
              </Group>

              <Group direction="vertical" desktopDirection="horizontal" gap={20} desktopGap={12}>
                <LeLiveGetTicketsButton hidden={hasRegistration} />
                <LeLiveAddToCalendar />
              </Group>
            </TextContainer>
          </Group>
        </LayoutContainer>
      </Sheet>
    </SectionWrapper>
  )
}

export default LeLiveEventDetails
